/**  Custom components stores services types constant */
import axiosInstance from '../../services/http.api';
import {DEFAULT_ERROR_MSG} from './../../utils/constants';
import { LoggedInUser } from '../User/user.types';

/** Document uploaded list data */
export interface DocumentUploadedData {
  files : string[]
}

/** Share file folder path */
export interface ShareFileFolderPath {
  labelingAgrementPath : string,
  folderPath : string
}

/** Share file folder path */
export interface EgnyteFolderPath {
  labelingAgrementPath : string,
  folderPath : string
}

/** Share file folder path */
export interface DownloadDocumentResponse {
  fileBlob : Blob
}

/**************** Constants ********************** */
const FETCH_DOCUMENTS_FAIL_MSG = "Failed to fetch document uploads.";
const UPLOAD_DOCUMENTS_FAIL_MSG = "Failed to upload documents.";
const DOWNLOAD_DOCUMENTS_FAIL_MSG = "An error occurred while downloading the document.";
const FILE_TYPE_NOT_SUPPORT_MSG = "Unsupported file type.";
const INVALID_FILE_NAME = "Invalid file name.";
const FILE_SIZE_LIMIT_MSG = "Please upload a smaller file. The allowed size limit is 10MB.";
const AGREEMENT_UPLOAD_SUCC_MSG = "Agreement uploaded successfully.";
const AGREEMENT_UPLOAD_FAIL_MSG = "Failed to upload the agreement.";
const AGREEMENT_ALLOWED_MIME_TYPES = ["application/pdf"];
const AGREEMENT_ALLOWED_EXTENSIONS = ["pdf"];
const AGREEMENT_FILE_MAX_SIZE_LIMIT = 10;

const  DOCUMENT_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const  DOCUMENT_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const DOCUMET_FILEPATH_NOT_PRESENT = "File upload path not available.";
const SHAREFILEPATH_GENERATION_ERROR = "Error while creating sharefile folder";
const EGNYTEILEPATH_GENERATION_ERROR = "Error while creating egnyte folder";

// Export all constants
export const DocumentUploadConstants = {
 FETCH_DOCUMENTS_FAIL_MSG,
 UPLOAD_DOCUMENTS_FAIL_MSG,
 FILE_TYPE_NOT_SUPPORT_MSG,
 FILE_SIZE_LIMIT_MSG,
 AGREEMENT_UPLOAD_SUCC_MSG,
 AGREEMENT_UPLOAD_FAIL_MSG,
 AGREEMENT_ALLOWED_MIME_TYPES,
 AGREEMENT_ALLOWED_EXTENSIONS,
 AGREEMENT_FILE_MAX_SIZE_LIMIT,
 DOCUMENT_ALLOWED_EXTENSIONS,
 DOCUMENT_ALLOWED_MIME_TYPES,
 DOCUMET_FILEPATH_NOT_PRESENT,
 EGNYTEILEPATH_GENERATION_ERROR,
 DOWNLOAD_DOCUMENTS_FAIL_MSG,
 INVALID_FILE_NAME
}

const SHAREFILE_BASE_URL = process.env.REACT_APP_SHAREFILE_BASE_URL || '';
const APP_ENV = process.env.REACT_APP_ENV || '';

export const documentUploadService = {

    /**
     * Fetch fiels from folderPath
     * @param folderPath 
     * @returns 
     */
    fetchAllUploadedDocuments: async (folderPath: string): Promise<DocumentUploadedData> => {
        try {

            if(!folderPath && folderPath != ""){
                throw new Error(FETCH_DOCUMENTS_FAIL_MSG);
            }

            const res = await axiosInstance.get<any>(`/v1/StorageApi/list/${folderPath}`);

            if (!res.data.isSuccess) {
                // Throw an error with custom message
                throw new Error(res.data.message || FETCH_DOCUMENTS_FAIL_MSG);
            }

            const data = res.data?.data || [];
            let filesNames = data.map((item: { name: string }) => item.name);
            const uploadDocumentList: DocumentUploadedData = { files : filesNames};
            return uploadDocumentList; 

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || FETCH_DOCUMENTS_FAIL_MSG);
        }
    },

    /**
     * Upload file
     * @param folderPath 
     * @param file 
     * @returns 
     */
    uploadFile: async (folderPath: string, file: any): Promise<any> => {
        try {
            if(!folderPath && folderPath != ""){
                throw new Error(DEFAULT_ERROR_MSG);
            }

            const formData = new FormData();
            formData.append("FolderPath", folderPath);
            formData.append("File", file);

            const res = await axiosInstance.post('/v1/StorageApi/upload', formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                },
            }); 

           if (!res.data.success) {
                // Throw an error with custom message
                throw new Error(res.data.message || DEFAULT_ERROR_MSG);
            }

            return file.name;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || UPLOAD_DOCUMENTS_FAIL_MSG);
        }
    },

    /**
     * Create folders in share file and return paths
     * @param customFolderPath 
     * @returns 
     */
    createFoldersInShareFile: async (customFolderPath: string, loginUserData: LoggedInUser): Promise<ShareFileFolderPath> => {
        try {
            if(!customFolderPath && customFolderPath != ""){
                throw new Error(DEFAULT_ERROR_MSG);
            }

            const url = `${SHAREFILE_BASE_URL}/api/ShareFile/create-folders-and-reset-permissions`;

            let folderPrefix = "";
            /*if( APP_ENV && APP_ENV.toUpperCase() === "STG"){
                folderPrefix = APP_ENV;
            }*/

            let folderPath = `${folderPrefix}${customFolderPath}`;

            type FolderPathItem = {
                path: string;
                Auditor_Email_s__c: Array<string>;
            };

            type RequestObj = {
                parentFolderId: string;
                folderPaths: FolderPathItem[];
            };

            let requestObj: RequestObj = {
                parentFolderId: 'allshared',
                folderPaths: []
            };

            let folderPathLabelingItem: FolderPathItem = {
                path: `${folderPath}/LabelingAgreement`,
                Auditor_Email_s__c: []
            };

            folderPathLabelingItem.Auditor_Email_s__c.push(loginUserData?.email);

            let folderPathObj: FolderPathItem = {
                path: folderPath,
                Auditor_Email_s__c: []
            };

            folderPathObj.Auditor_Email_s__c.push(loginUserData?.email);

            requestObj.folderPaths.push(folderPathLabelingItem);
            requestObj.folderPaths.push(folderPathObj);
           
            const res = await axiosInstance.post<any>(url, requestObj);

            if (!res) {
                // Throw an error with custom message
                throw new Error(DEFAULT_ERROR_MSG);
            }

            let responseData : ShareFileFolderPath = {
                labelingAgrementPath : res?.data?.result[folderPathLabelingItem.path],
                folderPath : res?.data?.result[folderPathObj.path]
            }

            return responseData;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || SHAREFILEPATH_GENERATION_ERROR);
        }
    },


    /**
     * Create folders in Egnyte for labelling agreement and documents and return paths
     * @param customFolderPath 
     * @returns 
     */
    createFoldersInEgnyte: async (customFolderPath: string, loginUserData: LoggedInUser): Promise<EgnyteFolderPath> => {
        try {
            if(!customFolderPath && customFolderPath != ""){
                throw new Error(DEFAULT_ERROR_MSG);
            }

        
            let folderPrefix = "";
            /*if( APP_ENV && APP_ENV.toUpperCase() === "STG"){
                folderPrefix = APP_ENV;
            }*/

            let folderPath = `${folderPrefix}${customFolderPath}`;

            let path = `${folderPath}/LabelingAgreement`;
           
            const reuqestData = {'folderPath':path};

            const res = await axiosInstance.post<any>('/v1/StorageApi/folder',reuqestData);

            if (!res.data.success) {
                // Throw an error with custom message
                throw new Error(res.data.message || DEFAULT_ERROR_MSG);
            }

            let responseData : EgnyteFolderPath = {
                labelingAgrementPath: path,
                folderPath : folderPath
            }

            return responseData;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || EGNYTEILEPATH_GENERATION_ERROR);
        }
    },


    /**
     * Download document and return blob
     * @param filePath 
     * @returns 
     */
    downloadDocument: async (filePath: string): Promise<any> => {
        try {
            if(!filePath && filePath != ""){
                throw new Error(DEFAULT_ERROR_MSG);
            }

            const response = await axiosInstance.get<any>(`/v1/StorageApi/download/${filePath}`,{responseType: "blob"}); 
            
            if (!response.data) {
                // Throw an error with custom message
                throw new Error(response.data.message || DEFAULT_ERROR_MSG);
            }

            const blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });

            
            let responseData : DownloadDocumentResponse = {
                fileBlob: blob
            }

            return responseData;

        } catch (error: any) {
            throw new Error(error?.response?.data?.message || EGNYTEILEPATH_GENERATION_ERROR);
        }
    }

}